import { log, shouldLogByProbability } from "../utils/logs";
import { ConsentCheckResults, ConsentMetaData } from "../types";
import { topmostAccessibleWindow } from "../utils";

const TCF_API_VERSION: number = 2;

declare interface TCData {
  gdprApplies: boolean;
  eventStatus?: string;
  tcString: string;
  purpose?: {
    consents: {
      [key: number]: boolean;
    };
  };
}

async function getConsent(): Promise<TCData> {
  return new Promise((resolve, reject) => {
    if (typeof topmostAccessibleWindow?.__tcfapi === "function") {
      const callback = (tcData: TCData, success: boolean) => {
        if (success) {
          resolve(tcData);
        } else {
          reject("failed to get TCData");
        }
      };

      // eslint-disable-next-line
      // @ts-ignore
      topmostAccessibleWindow.__tcfapi(
        "addEventListener",
        TCF_API_VERSION,
        callback,
      );
    }
    reject("tcfapi not found");
  });
}

// The eventStatus property of the TCData object shall be one of the following:
// - "tcloaded": CMP is loaded and is prepared to surface a TC String to any calling scripts on the page -
// this event status going to be returned on each next page views after user interacted with consent message.
// - "useractioncomplete": whenever a user has confirmed or re-confirmed their choices -
// this event status going to be returned in the same page view after user interacted with consent message
// - "cmpuishown": the UI is surfaced or re-surfaced
const consentShownInCurrentPV = (tcData: TCData) => {
  if (tcData?.eventStatus === "tcloaded") {
    return false;
  }
  if (
    tcData?.eventStatus === "useractioncomplete" ||
    tcData?.eventStatus === "cmpuishown"
  ) {
    return true;
  }

  // if we fall here it means that tcData missed or has unexpected event status, this actually should never happen
  // but if so, we're going to hande this as if TCF API failed
  return false;
};

const isConsent = (tcData: TCData) => {
  if (!tcData) {
    // if tcData is not find, we can't determine whether or not the user has consented
    return false;
  }
  if (tcData.gdprApplies === false) {
    // if gdpr is not applies there no need to check consents
    return true;
  }

  return !!(
    !allFalse(tcData.purpose?.consents) &&
    isRequiredConsentGiven(tcData.purpose)
  );
};

const allFalse = (obj = {}) => !Object.values(obj).some(Boolean);

const isRequiredConsentGiven = (purpose) => {
  const requirePurposes = [1, 2, 3, 4, 7, 9, 10];
  return requirePurposes.every((key) => purpose?.consents?.[key]);
};

const processConsentData = (tcData: TCData): ConsentCheckResults => {
  const consentMeta: ConsentMetaData = {
    tcDataDefined: !!tcData,
    gdprApplies: tcData?.gdprApplies,
  };

  if (!tcData || tcData.gdprApplies === false) {
    // if no TC Data found or GDPR not applies it means that render is allowed regardless of consent given
    return {
      consentShownInCurrentPV: false,
      isConsentGiven: true,
      meta: consentMeta,
    } as ConsentCheckResults;
  }

  return {
    consentShownInCurrentPV: consentShownInCurrentPV(tcData),
    isConsentGiven: isConsent(tcData),
    meta: consentMeta,
  } as ConsentCheckResults;
};

// define if consent message appears.
export async function getConsentData() {
  return getConsent()
    .then((tcData: TCData) => {
      return processConsentData(tcData);
    })
    .catch((reason) => {
      if (shouldLogByProbability()) {
        log(
          "rlink.getConsent",
          {
            pageURL: topmostAccessibleWindow.location.href,
            error: reason,
          },
          { severity: 3 },
        );
      }

      // if __tcfapi not available RL message should be rendered assuming that consent given
      return {
        consentShownInCurrentPV: false,
        isConsentGiven: true,
        meta: {
          tcDataDefined: false,
        },
      } as ConsentCheckResults;
    });
}
